import { RefObject, useCallback, useEffect, useState } from 'react';

import ResizeObserverPolyfill from 'resize-observer-polyfill';

/** Absorbs sub-pixel scroll offsets on fractional zoom and HiDPI displays. */
const EDGE_TOLERANCE_PX = 1;

const ResizeObserver_ =
  typeof ResizeObserver !== 'undefined'
    ? ResizeObserver
    : ResizeObserverPolyfill;

export interface IHorizontalScrollEdges {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  /** Wire to the scroller's `onScroll`. */
  onScroll: () => void;
}

/**
 * Tracks which directions a horizontal scroller can still travel in.
 *
 * Both flags are gated on there being any overflow at all, so "no arrows when
 * everything fits" needs no separate state.
 *
 * @param ref the scrolling element
 * @param contentKey re-measure when the rendered content changes, e.g. once the
 * sectors have loaded. `ResizeObserver` alone is not enough: it is a no-op under
 * jest, and in a browser fonts can settle after mount.
 */
export const useHorizontalScrollEdges = (
  ref: RefObject<HTMLElement>,
  contentKey: unknown
): IHorizontalScrollEdges => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const measure = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const hasOverflow = maxScrollLeft > EDGE_TOLERANCE_PX;

    setCanScrollLeft(hasOverflow && element.scrollLeft > EDGE_TOLERANCE_PX);
    setCanScrollRight(
      hasOverflow && element.scrollLeft < maxScrollLeft - EDGE_TOLERANCE_PX
    );
  }, [ref]);

  useEffect(() => {
    measure();

    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver_(measure);
    observer.observe(element);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [contentKey, measure, ref]);

  // `scroll` fires throughout a smooth-scroll animation, so the edge state
  // tracks the animation without any timers.
  return { canScrollLeft, canScrollRight, onScroll: measure };
};
